import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  ChevronUp,
  ChevronDown,
  Trash2,
  Eye,
  EyeOff,
  Save,
  DoorOpen,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { EVENTO_SALONES_AG } from "@/constants/testIds";
import { useSalonesOficiales } from "@/data/salonesCatalogoStore";
import {
  listarSalonesAcceso,
  crearSalonAcceso,
  actualizarSalonAcceso,
  activarSalonAcceso,
  desactivarSalonAcceso,
  eliminarSalonAcceso,
  ordenarSalones,
} from "@/data/salonesAccesoStore";

const fmtMoneda = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "$ —";
  return num.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const errTexto = (res) => {
  if (!res || !res.error) return "";
  if (typeof res.error === "string") {
    if (res.error === "network")
      return "No se pudo conectar con el servidor. Intenta más tarde.";
    return res.error;
  }
  if (typeof res.error === "object") {
    if (res.error.reason === "CAPACIDAD_MENOR_QUE_RESERVADO") {
      return `No puedes reducir la capacidad: ya hay ${res.error.reservado} lugar(es) reservado(s) en este salón.`;
    }
    return res.error.detail || "No se pudo guardar el cambio.";
  }
  return "No se pudo guardar el cambio.";
};

export default function PasoSalonesAcceso({ valores, onChange }) {
  // Sugerencias del datalist: catálogo oficial backend (texto libre permitido).
  const { salones: salonesOficiales } = useSalonesOficiales();
  const eventoId = valores?.id || null;
  const [salones, setSalones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const [borrador, setBorrador] = useState({}); // ediciones locales por id
  const [nuevo, setNuevo] = useState({ salon: "", capacidad: "", precio: "" });
  const [nuevoError, setNuevoError] = useState("");
  const [aEliminar, setAEliminar] = useState(null);
  const [eliminarSoloDesactivar, setEliminarSoloDesactivar] = useState(false);

  const recargar = useCallback(async () => {
    if (!eventoId) {
      setSalones([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    const lista = ordenarSalones(await listarSalonesAcceso(eventoId));
    setSalones(lista);
    setBorrador({});
    setCargando(false);
    onChange && onChange("salonesAcceso", lista);
  }, [eventoId, onChange]);

  useEffect(() => {
    recargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  const ordenados = useMemo(() => ordenarSalones(salones), [salones]);

  const nombresExistentes = (excluirId) =>
    ordenados
      .filter((s) => s.id !== excluirId)
      .map((s) => (s.salon || "").trim().toLowerCase());

  const valNombre = (nombre, excluirId) => {
    const t = (nombre || "").trim();
    if (!t) return "El nombre del salón es obligatorio.";
    if (nombresExistentes(excluirId).includes(t.toLowerCase()))
      return "Ya existe un salón con ese nombre en este evento.";
    return "";
  };
  const valCapacidad = (cap) => {
    const n = Number(cap);
    if (!Number.isInteger(n) || n <= 0)
      return "La capacidad debe ser un entero mayor que cero.";
    return "";
  };
  const valPrecio = (p) => {
    const n = Number(p);
    if (!Number.isFinite(n) || n <= 0) return "El precio debe ser mayor que cero.";
    if (!/^\d+(\.\d{1,2})?$/.test(String(p))) return "Máximo dos decimales.";
    return "";
  };

  const agregar = async () => {
    const eNombre = valNombre(nuevo.salon, null);
    const eCap = valCapacidad(nuevo.capacidad);
    const ePrecio = valPrecio(nuevo.precio);
    const msg = eNombre || eCap || ePrecio;
    if (msg) {
      setNuevoError(msg);
      return;
    }
    setNuevoError("");
    setGuardando(true);
    const orden = ordenados.length
      ? Math.max(...ordenados.map((s) => s.orden ?? 0)) + 1
      : 0;
    const res = await crearSalonAcceso(eventoId, {
      salon: nuevo.salon.trim(),
      capacidad: Number(nuevo.capacidad),
      precioPorBoleto: Number(nuevo.precio),
      orden,
      activo: true,
    });
    setGuardando(false);
    if (res?.error) {
      setNuevoError(errTexto(res));
      return;
    }
    toast.success(`Salón «${res.salon}» agregado.`);
    setNuevo({ salon: "", capacidad: "", precio: "" });
    await recargar();
  };

  const editarLocal = (id, campo, valor) => {
    setBorrador((b) => ({ ...b, [id]: { ...(b[id] || {}), [campo]: valor } }));
    setErrores((e) => (e[id] ? { ...e, [id]: undefined } : e));
  };

  const valorActual = (s, campo, fallback) => {
    const b = borrador[s.id];
    return b && b[campo] !== undefined ? b[campo] : fallback;
  };

  const guardarFila = async (s) => {
    const b = borrador[s.id] || {};
    const nombre = valorActual(s, "salon", s.salon);
    const cap = valorActual(s, "capacidad", s.capacidad);
    const precio = valorActual(s, "precioPorBoleto", s.precioPorBoleto);
    const eNombre = valNombre(nombre, s.id);
    const eCap = valCapacidad(cap);
    const ePrecio = valPrecio(precio);
    const msg = eNombre || eCap || ePrecio;
    if (msg) {
      setErrores((e) => ({ ...e, [s.id]: msg }));
      return;
    }
    setGuardando(true);
    const res = await actualizarSalonAcceso(eventoId, s.id, {
      salon: String(nombre).trim(),
      capacidad: Number(cap),
      precioPorBoleto: Number(precio),
    });
    setGuardando(false);
    if (res?.error) {
      setErrores((e) => ({ ...e, [s.id]: errTexto(res) }));
      return;
    }
    const cambioPrecio = Number(precio) !== Number(s.precioPorBoleto);
    toast.success(`Salón «${res.salon}» actualizado.`);
    if (cambioPrecio) {
      toast.info("El nuevo precio aplicará únicamente a compras futuras.");
    }
    await recargar();
  };

  const mover = async (s, dir) => {
    const idx = ordenados.findIndex((x) => x.id === s.id);
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= ordenados.length) return;
    const vecino = ordenados[j];
    setGuardando(true);
    const r1 = await actualizarSalonAcceso(eventoId, s.id, { orden: vecino.orden ?? j });
    const r2 = await actualizarSalonAcceso(eventoId, vecino.id, { orden: s.orden ?? idx });
    setGuardando(false);
    if (r1?.error || r2?.error) {
      toast.error("No se pudo cambiar el orden.");
      return;
    }
    await recargar();
  };

  const toggleActivo = async (s) => {
    setGuardando(true);
    const res = s.activo
      ? await desactivarSalonAcceso(eventoId, s.id)
      : await activarSalonAcceso(eventoId, s.id);
    setGuardando(false);
    if (res?.error) {
      toast.error(errTexto(res));
      return;
    }
    toast.success(s.activo ? "Salón desactivado." : "Salón activado.");
    await recargar();
  };

  const confirmarEliminar = async () => {
    if (!aEliminar) return;
    if (eliminarSoloDesactivar) {
      await toggleActivo(aEliminar);
      setAEliminar(null);
      setEliminarSoloDesactivar(false);
      return;
    }
    setGuardando(true);
    const res = await eliminarSalonAcceso(eventoId, aEliminar.id);
    setGuardando(false);
    if (res?.error) {
      // 409 por historial → ofrecer desactivar en lugar de eliminar.
      if (res.status === 409) {
        setEliminarSoloDesactivar(true);
        toast.warning(
          "El salón tiene compras/boletos asociados; no puede eliminarse. Puedes desactivarlo para conservar el historial.",
        );
        return;
      }
      toast.error(errTexto(res));
      return;
    }
    toast.success("Salón eliminado.");
    setAEliminar(null);
    await recargar();
  };

  if (!eventoId) {
    return (
      <div
        data-testid={EVENTO_SALONES_AG.screen}
        className="rounded-2xl border hairline bg-card p-6 text-sm text-muted-foreground"
      >
        Guarda el borrador para poder configurar los salones de Acceso General.
      </div>
    );
  }

  return (
    <div data-testid={EVENTO_SALONES_AG.screen} className="flex flex-col gap-6">
      <header>
        <span className="overline text-accent">Paso 3 · Acceso General</span>
        <h2 className="mt-1 font-heading text-2xl sm:text-3xl leading-tight">
          Salones y boletos
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Los socios seleccionarán uno o varios salones y la cantidad de boletos,
          sin elegir mesa. Configura cada salón con su capacidad vendible y precio.
        </p>
      </header>

      {/* Alta de salón */}
      <section className="rounded-2xl border hairline bg-card p-5 flex flex-col gap-4">
        <p className="overline text-muted-foreground">Agregar salón</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr,140px,160px,auto] gap-3 items-end">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">Nombre del salón</span>
            <Input
              list="ag-salones-sugeridos"
              data-testid={EVENTO_SALONES_AG.nuevoNombre}
              value={nuevo.salon}
              onChange={(e) => setNuevo((n) => ({ ...n, salon: e.target.value }))}
              placeholder="Ej. Salón Miramar"
              className="rounded-xl"
            />
            <datalist id="ag-salones-sugeridos">
              {salonesOficiales.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">Capacidad</span>
            <Input
              type="number"
              min="1"
              step="1"
              data-testid={EVENTO_SALONES_AG.nuevoCapacidad}
              value={nuevo.capacidad}
              onChange={(e) => setNuevo((n) => ({ ...n, capacidad: e.target.value }))}
              placeholder="0"
              className="rounded-xl"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">Precio por boleto (MXN)</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              data-testid={EVENTO_SALONES_AG.nuevoPrecio}
              value={nuevo.precio}
              onChange={(e) => setNuevo((n) => ({ ...n, precio: e.target.value }))}
              placeholder="0.00"
              className="rounded-xl"
            />
          </label>
          <Button
            type="button"
            data-testid={EVENTO_SALONES_AG.nuevoGuardar}
            onClick={agregar}
            disabled={guardando}
            className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Agregar
          </Button>
        </div>
        {nuevoError && (
          <p
            data-testid={EVENTO_SALONES_AG.nuevoError}
            className="text-xs text-destructive"
          >
            {nuevoError}
          </p>
        )}
      </section>

      {/* Lista de salones */}
      {cargando ? (
        <div
          data-testid={EVENTO_SALONES_AG.cargando}
          className="rounded-2xl border hairline bg-card p-6 flex items-center gap-3 text-sm text-muted-foreground"
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando salones…
        </div>
      ) : ordenados.length === 0 ? (
        <div
          data-testid={EVENTO_SALONES_AG.vacio}
          className="rounded-2xl border hairline bg-card p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-3"
        >
          <DoorOpen className="w-8 h-8 text-muted-foreground/60" strokeWidth={1.5} />
          Aún no hay salones. Agrega al menos uno para poder publicar el evento.
        </div>
      ) : (
        <ul data-testid={EVENTO_SALONES_AG.lista} className="flex flex-col gap-3">
          {ordenados.map((s, i) => {
            const inactivo = !s.activo;
            const err = errores[s.id];
            const precioActual = valorActual(s, "precioPorBoleto", s.precioPorBoleto);
            const cambioPrecioPendiente =
              Number(precioActual) !== Number(s.precioPorBoleto);
            return (
              <li
                key={s.id}
                data-testid={EVENTO_SALONES_AG.row(s.id)}
                data-activo={s.activo ? "true" : "false"}
                className={cn(
                  "rounded-2xl border hairline bg-card p-4 flex flex-col gap-3",
                  inactivo && "opacity-80 border-dashed",
                )}
              >
                <div className="flex flex-col lg:flex-row lg:items-end gap-3">
                  {/* Orden */}
                  <div className="flex lg:flex-col gap-1 lg:pt-5">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label={`Subir ${s.salon}`}
                      data-testid={EVENTO_SALONES_AG.btnSubir(s.id)}
                      disabled={i === 0 || guardando}
                      onClick={() => mover(s, "up")}
                      className="h-7 w-7 rounded-lg"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label={`Bajar ${s.salon}`}
                      data-testid={EVENTO_SALONES_AG.btnBajar(s.id)}
                      disabled={i === ordenados.length - 1 || guardando}
                      onClick={() => mover(s, "down")}
                      className="h-7 w-7 rounded-lg"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </div>

                  <label className="flex-1 flex flex-col gap-1.5 min-w-0">
                    <span className="text-xs text-muted-foreground">Nombre</span>
                    <Input
                      data-testid={EVENTO_SALONES_AG.rowNombre(s.id)}
                      value={valorActual(s, "salon", s.salon)}
                      onChange={(e) => editarLocal(s.id, "salon", e.target.value)}
                      className="rounded-xl"
                    />
                  </label>
                  <label className="flex flex-col gap-1.5 w-full lg:w-32">
                    <span className="text-xs text-muted-foreground">Capacidad</span>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      data-testid={EVENTO_SALONES_AG.rowCapacidad(s.id)}
                      value={valorActual(s, "capacidad", s.capacidad)}
                      onChange={(e) => editarLocal(s.id, "capacidad", e.target.value)}
                      className="rounded-xl"
                    />
                  </label>
                  <label className="flex flex-col gap-1.5 w-full lg:w-40">
                    <span className="text-xs text-muted-foreground">Precio (MXN)</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      data-testid={EVENTO_SALONES_AG.rowPrecio(s.id)}
                      value={valorActual(s, "precioPorBoleto", s.precioPorBoleto)}
                      onChange={(e) =>
                        editarLocal(s.id, "precioPorBoleto", e.target.value)
                      }
                      className="rounded-xl"
                    />
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={EVENTO_SALONES_AG.btnGuardarRow(s.id)}
                    onClick={() => guardarFila(s)}
                    disabled={guardando}
                    className="rounded-xl"
                  >
                    <Save className="w-4 h-4 mr-1.5" />
                    Guardar
                  </Button>
                </div>

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span
                      data-testid={EVENTO_SALONES_AG.rowEstado(s.id)}
                      className={cn(
                        "inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 ring-1",
                        s.activo
                          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-500/40"
                          : "bg-muted text-muted-foreground ring-border",
                      )}
                    >
                      {s.activo ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5" />
                      )}
                      {s.activo ? "Activo · en venta" : "Inactivo · fuera de venta"}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {fmtMoneda(s.precioPorBoleto)} · {s.capacidad} lugares
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid={
                        s.activo
                          ? EVENTO_SALONES_AG.btnDesactivar(s.id)
                          : EVENTO_SALONES_AG.btnActivar(s.id)
                      }
                      onClick={() => toggleActivo(s)}
                      disabled={guardando}
                      className="rounded-lg"
                    >
                      {s.activo ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-1.5" /> Desactivar
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-1.5" /> Activar
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid={EVENTO_SALONES_AG.btnEliminar(s.id)}
                      onClick={() => {
                        setAEliminar(s);
                        setEliminarSoloDesactivar(false);
                      }}
                      disabled={guardando}
                      className="rounded-lg text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar
                    </Button>
                  </div>
                </div>

                {cambioPrecioPendiente && (
                  <p
                    data-testid={EVENTO_SALONES_AG.notaPrecio(s.id)}
                    className="text-[11px] text-accent"
                  >
                    El nuevo precio aplicará únicamente a compras futuras.
                  </p>
                )}
                {err && (
                  <p
                    data-testid={EVENTO_SALONES_AG.rowError(s.id)}
                    className="text-xs text-destructive"
                  >
                    {err}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Diálogo eliminar / desactivar */}
      <Dialog
        open={!!aEliminar}
        onOpenChange={(o) => {
          if (!o) {
            setAEliminar(null);
            setEliminarSoloDesactivar(false);
          }
        }}
      >
        <DialogContent data-testid={EVENTO_SALONES_AG.dialogEliminar}>
          <DialogHeader>
            <DialogTitle>
              {eliminarSoloDesactivar ? "No se puede eliminar" : "Eliminar salón"}
            </DialogTitle>
            <DialogDescription>
              {eliminarSoloDesactivar
                ? `El salón «${aEliminar?.salon}» tiene historial de compras o boletos. Consérvalo desactivándolo para no perder registros.`
                : `¿Seguro que deseas eliminar el salón «${aEliminar?.salon}»? Esta acción no se puede deshacer.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid={EVENTO_SALONES_AG.dialogEliminarCancelar}
              onClick={() => {
                setAEliminar(null);
                setEliminarSoloDesactivar(false);
              }}
            >
              Cancelar
            </Button>
            {eliminarSoloDesactivar ? (
              <Button
                data-testid={EVENTO_SALONES_AG.dialogEliminarDesactivar}
                onClick={confirmarEliminar}
                className="bg-primary text-primary-foreground"
              >
                <EyeOff className="w-4 h-4 mr-1.5" /> Desactivar salón
              </Button>
            ) : (
              <Button
                variant="destructive"
                data-testid={EVENTO_SALONES_AG.dialogEliminarConfirmar}
                onClick={confirmarEliminar}
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
